import { useState } from 'react';
import { Share2 } from 'lucide-react';
import {
  DISPATCH_CARD_TITLE,
  dispatchCardCaption,
  dispatchCardFileName,
  paginateDispatchCard,
} from '../../core/dispatch-card';
import type { DispatchCard, DispatchCardPage } from '../../core/dispatch-card';
import { dispatchCardImageFiles } from '../../core/dispatch-card-image';

interface ShareDispatchButtonProps {
  card: DispatchCard;
  /** The board's card row wants a shorter button than a detail screen does. */
  size?: 'sm' | 'lg';
}

/** The label of the one button a manager actually looks for. */
export const SHARE_DISPATCH_LABEL = '📲 שתף לוואטסאפ';

/** Hands a blob to the browser as a downloaded file. */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // The object URL is released on the next tick — Safari needs the click first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Puts the caption on the clipboard; a browser that refuses is not a failure. */
async function copyToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard?.writeText(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * "📲 שתף לוואטסאפ" — the one share action, image and caption together.
 *
 * A single `navigator.share({ files, text })` call: the portrait order as a
 * PNG, and beside it the caption WhatsApp prints under the image — the bold
 * mission title and, when the task has one, the field contact. A convoy of
 * four or more vehicles is several images, and all of them go into that same
 * call, so the whole order lands in the chat at once.
 *
 * There is no second share path. The old download button and formatted-text
 * link were two more ways to send the same order, each one able to drift from
 * the image; a desktop browser with no file share now saves the images and
 * copies the caption from inside this action, and the manager still sends one
 * message.
 */
export function ShareDispatchButton({ card, size = 'sm' }: ShareDispatchButtonProps) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pageCount = paginateDispatchCard(card).length;
  const nameOf = (page: DispatchCardPage) => dispatchCardFileName(card, page);

  const shareOrder = async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const files = await dispatchCardImageFiles(card, nameOf);
      if (files.length === 0) throw new Error('הדפדפן אינו תומך ביצירת התמונה');
      const caption = dispatchCardCaption(card);
      const share = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
      };
      // One payload: every page of the order plus the caption that describes it.
      const payload: ShareData = { files, title: DISPATCH_CARD_TITLE, text: caption };
      if (share.share && share.canShare?.(payload)) {
        await share.share(payload);
        return;
      }
      // No native file share: the same two things, handed over separately.
      for (const file of files) saveBlob(file, file.name);
      const copied = await copyToClipboard(caption);
      const images = files.length > 1 ? `${files.length} התמונות הורדו` : 'התמונה הורדה';
      setNote(
        copied
          ? `הדפדפן אינו תומך בשיתוף קבצים — ${images} וטקסט הליווי הועתק ללוח.`
          : `הדפדפן אינו תומך בשיתוף קבצים — ${images} למכשיר.`,
      );
    } catch (err) {
      // A user dismissing the system share sheet is not an error.
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(`שיתוף המשימה נכשל — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const primary = [
    'w-full rounded-2xl border border-emerald-600 bg-emerald-600 text-white font-semibold',
    'flex items-center justify-center gap-2 disabled:opacity-50',
    size === 'lg' ? 'h-14 text-base' : 'h-11 text-sm',
  ].join(' ');

  return (
    <div data-print="hide" className="w-full space-y-1.5">
      <button type="button" onClick={() => void shareOrder()} disabled={busy} className={primary}>
        <Share2 size={size === 'lg' ? 19 : 16} aria-hidden />
        {busy ? 'מכין את התמונה…' : SHARE_DISPATCH_LABEL}
      </button>

      {pageCount > 1 ? (
        <p className="text-xs text-ink/50">
          שיירה של {card.vehicleCount} רכבים — הפקודה תישלח כ־{pageCount} תמונות, כולן יחד.
        </p>
      ) : null}

      {note ? <p className="text-xs text-ink/50">{note}</p> : null}
      {error ? (
        <p className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">{error}</p>
      ) : null}
    </div>
  );
}
